import * as RadioGroupR from "@radix-ui/react-radio-group";
import classNames from "classnames";
import { forwardRef, ReactNode } from "react";

export type ContentProps = {
  children?: ReactNode;
  className?: string;
  text?: string;
};

export const RadioCircleDesign = forwardRef(
  (
    {
      children,
      disabled,
      size = "regular",
      ...rest
    }: {
      children: ReactNode;
      disabled?: boolean;
      size?: "regular" | "small";
    },
    ref: React.Ref<HTMLDivElement>
  ) => (
    <div
      {...rest}
      ref={ref}
      data-disabled={disabled ? "" : undefined}
      className={classNames(
        "all:unset t-relative t-rounded-full t-outline-none t-cursor-pointer t-border t-border-solid t-flex t-justify-center t-items-center hover:t-border-purple-30 hover:t-border-1 hover:t-bg-surface-purple data-[disabled]:t-bg-surface-lighter-grey data-[disabled]:t-cursor-not-allowed data-[disabled]:t-border-neutral-10 data-[disabled]:hover:t-bg-surface-lighter-grey data-[disabled]:hover:t-border-neutral-10 data-[disabled]:hover:t-border-1",
        {
          "t-border-neutral-20": !disabled,
          "t-min-w-[16px] t-min-h-[16px]": size === "regular",
          "t-w-4 t-h-4": size === "small",
        }
      )}
    >
      {children}
    </div>
  )
);

export const RadioIndicatorDesign = forwardRef(
  (props, ref: React.Ref<HTMLDivElement>) => (
    <div
      {...props}
      ref={ref}
      className="t-w-2 t-h-2 t-bg-white t-rounded-[50%]"
    />
  )
);

export const RadioGroupItem = ({
  value,
  disabled,
  ...rest
}: { value: string } & RadioGroupR.RadioGroupItemProps) => {
  return (
    <RadioGroupR.Item
      {...rest}
      value={value}
      disabled={disabled}
      className="all:unset t-p-[2px] t-cursor-pointer"
    >
      <RadioCircleDesign disabled={disabled}>
        <RadioGroupR.Indicator className="t-absolute t-inset-[-1px] t-rounded-full t-bg-purple hover:t-bg-purple-50 data-[disabled]:t-bg-neutral-10 t-flex t-items-center t-justify-center">
          <RadioIndicatorDesign />
        </RadioGroupR.Indicator>
      </RadioCircleDesign>
    </RadioGroupR.Item>
  );
};

const Item = ({
  children,
  description,
  className = "",
  value = "",
  asChild,
  align,
  inline = true,
  ...props
}: RadioGroupR.RadioGroupItemProps &
  React.RefAttributes<HTMLDivElement> &
  ContentProps & {
    description?: ReactNode;
    align?: "start" | "center" | "end";
    inline?: boolean;
  }) => {
  const alignClass = `t-items-${align ?? (description ? "start" : "center")}`;

  return (
    <label
      className={classNames("t-flex t-gap-2 t-w-full", alignClass, className, {
        "t-h-5": inline && !description,
      })}
      htmlFor={props.id || value}
      aria-label={props["aria-label"] || undefined}
    >
      {description ? (
        <div className="t-mt-px">
          <RadioGroupItem {...props} value={value} id={props.id || value} />
        </div>
      ) : (
        <RadioGroupItem {...props} value={value} id={props.id || value} />
      )}
      <div
        className={classNames(
          "t-w-full t-text-text-60 t-cursor-pointer t-font-sans",
          {
            "t-text-subtext": !!description,
            "t-text-body-sm": !description,
            "t-opacity-50 t-cursor-no-drop": props.disabled,
            "t-flex t-flex-col t-gap-0.5": !!description,
          }
        )}
      >
        <span>{children}</span>
        {description && (
          <div className="t-font-sans t-text-body-sm !t-font-light t-text-text-30">
            {description}
          </div>
        )}
      </div>
    </label>
  );
};

const Content = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={classNames("t-flex t-flex-col", {
        [className]: className,
        "t-gap-2": !className,
      })}
    >
      {children}
    </div>
  );
};

const TileItem = ({
  value,
  className = "",
  disabled,
  ...props
}: RadioGroupR.RadioGroupItemProps & ContentProps & { subtext: string }) => {
  return (
    <RadioGroupR.Item
      {...props}
      value={value || ""}
      disabled={disabled}
      className={classNames(
        "t-group all:unset t-w-full t-box-border t-cursor-pointer t-rounded-lg t-border t-border-solid t-border-neutral-10 t-text-left t-transition-colors",
        {
          "t-cursor-not-allowed t-bg-white": disabled,
          "data-state-checked:t-border-purple data-state-checked:t-bg-purple-0 hover:t-bg-surface-lighter-grey":
            !disabled,
          [className]: className,
        }
      )}
    >
      <div className="t-flex t-items-start t-gap-2 group-data-[disabled]:t-cursor-not-allowed t-p-3">
        <div className="t-pt-[1.5px] group-hover:[&>div]:t-border-purple-30 group-hover:[&>div]:t-bg-surface-purple">
          <RadioCircleDesign disabled={disabled}>
            <RadioGroupR.Indicator className="t-absolute t-inset-[-1px] t-rounded-full t-bg-purple hover:t-bg-purple-50 data-[disabled]:t-bg-neutral-10 t-flex t-items-center t-justify-center">
              <RadioIndicatorDesign />
            </RadioGroupR.Indicator>
          </RadioCircleDesign>
        </div>
        <div className="t-flex t-flex-col t-justify-center t-items-start t-gap-0.5 ">
          <span className="group-data-[disabled]:t-text-text-30 t-text-text-60 t-text-subtext">
            {props.title}
          </span>
          <div className="group-data-[disabled]:t-text-neutral-30 t-text-text-30 t-text-body-sm-lite t-text-">
            {props.subtext}
          </div>
        </div>
      </div>
    </RadioGroupR.Item>
  );
};

const RadioGroup = {
  ...RadioGroupR,
  Item: Item,
  TileItem: TileItem,
  Content: Content,
};

export default RadioGroup;
